import type { QueryAssetInput } from '@scio3/shared-types';
import { ASSET_TYPES, FILE_TYPES } from '@scio3/shared-types';

interface FilterState {
  fileType: string[];
  tags: string[];
  searchText: string;
  searchScope: 'all' | 'folder';
  /** When set, filters child assets of a cloud storage root asset (DRIVE mode) */
  assetRootId?: string;
}

/**
 * Build the "Apps" filter query.
 * Apps are assets that match:
 *   (type='file' AND (commonType!=null OR appType!=null))
 *   OR fileType='web'
 *   OR type IN ['app', 'splitScreen']  // Split Screen + small apps (Video Wall, Live TV, etc.)
 */
function buildAppsFilter(): QueryAssetInput {
  return {
    OR: [
      {
        // type='file' AND (commonType!=null OR appType!=null)
        type: ASSET_TYPES.FILE,
        OR: [{ commonType_ne: null }, { appType_ne: null }],
      },
      {
        // fileType='web'
        fileType: FILE_TYPES.WEB,
      },
      {
        // top-level app types (Split Screen, Video Wall, Live TV, etc.)
        type_in: [ASSET_TYPES.APP, ASSET_TYPES.SPLIT_SCREEN],
      },
    ],
  };
}

export function buildQueryInput(
  filters: FilterState,
  currentPath: string,
  teamId?: string,
  /**
   * Favorites location: show EVERY favorited asset regardless of which folder it lives in
   * (including items nested inside a favorited folder). Omit the path filter entirely — a
   * `path:null` would restrict to root, and any nested favorite has a real folder path.
   */
  isFavorites = false,
): QueryAssetInput {
  const query: QueryAssetInput = {};

  // DRIVE mode: filter by assetRootId, skip path (children may be nested anywhere)
  if (filters.assetRootId) {
    query.assetRootId = filters.assetRootId;
    query.isDisable = true;
    // Apply file type filter within drive content (falls through to fileType block below)
    // but skip path/search/tags — return early after applying type filter
    if (filters.fileType.length > 0) {
      const hasWebFilter = filters.fileType.includes(FILE_TYPES.WEB);
      const otherTypes = filters.fileType.filter((t) => t !== FILE_TYPES.WEB);
      if (hasWebFilter && otherTypes.length === 0) {
        const appsFilter = buildAppsFilter();
        query.OR = appsFilter.OR;
      } else if (hasWebFilter && otherTypes.length > 0) {
        const appsFilter = buildAppsFilter();
        query.OR = [...(appsFilter.OR || []), { fileType_in: otherTypes }];
      } else {
        query.fileType_in = filters.fileType;
      }
    }
    return query;
  }

  // Path filter — only filter by path if in folder scope or browsing a folder
  // Root assets have path:null in MongoDB (not "/"), so send null for root.
  // Skipped entirely in the Favorites location (all favorites, any folder).
  if (!isFavorites && (filters.searchScope === 'folder' || !filters.searchText)) {
    const resolvedPath = currentPath === '/' ? null : currentPath;
    query.path = resolvedPath;
    // query.teamId coupled with path — only set when path is truthy
    if (teamId && resolvedPath) query.teamId = teamId;
  }

  // Exclude engage assets from the normal asset view (they appear in Engage page)
  query.engage_ne = true;

  // Search text → name regex
  if (filters.searchText) {
    query.originalFileName_contains = filters.searchText;
  }

  // File type filter (image, video, document, web/apps, audio)
  if (filters.fileType.length > 0) {
    const hasWebFilter = filters.fileType.includes(FILE_TYPES.WEB);
    const otherTypes = filters.fileType.filter((t) => t !== FILE_TYPES.WEB);

    if (hasWebFilter && otherTypes.length === 0) {
      // Only "Apps" (web) filter selected — use the complex Apps filter
      const appsFilter = buildAppsFilter();
      query.OR = appsFilter.OR;
    } else if (hasWebFilter && otherTypes.length > 0) {
      // "Apps" plus other types selected — combine with OR
      const appsFilter = buildAppsFilter();
      query.OR = [
        // Apps filter conditions
        ...(appsFilter.OR || []),
        // Other file types (image, video, document, audio)
        { fileType_in: otherTypes },
      ];
    } else {
      // No "Apps" filter — use simple fileType_in
      query.fileType_in = filters.fileType;
    }
  }

  // Tags filter
  if (filters.tags.length > 0) {
    query.tags_in = filters.tags;
  }

  // Note: favoritesOnly is a control flag sent via extendParams at the hook level (not a query
  // field), and the "rest" exclusion is the sibling extendParams.excludeFavorites flag (server-resolved _id $nin).

  return query;
}
